import React from 'react';
import {
  Button,
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
} from '@material-ui/core';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PayWithStripeDialog from './PayWithStripeDialog';
import AuthUtil from '../../redux/AuthUtil';
import { STRIPE as STRIPE_ENDPOINTS } from '../global/Server/endpoints';

/**
 * Phase 2: Payment Methods card. Drops into the per-location Profile view.
 *
 * Restricted to the three billing roles (ROLE_OWNER / ROLE_TECH_ADMIN /
 * ROLE_MASTER_ADMIN) — the parent component is responsible for the gate;
 * this component just hides itself if it isn't given billing access.
 *
 * Operations (card management ONLY — no charges happen here):
 *   - List saved cards on mount + after every mutation.
 *   - Mark a card default → off-session auto-renewal uses this one.
 *   - Remove a card.
 *   - Add Card → opens PayWithStripeDialog in save_card mode (SetupIntent;
 *     no charge happens).
 *
 * On-demand payments live in Payment History (Pay button on UNPAID rows).
 * The 15th-of-month cron uses the default card for automatic renewals.
 *
 * Enterprise hardening:
 *   - Buttons disable during in-flight mutations.
 *   - Single mutation at a time (mutating ref guard).
 *   - Errors surface inline; no toast spam.
 *   - Refetches list after every mutation so the UI stays consistent.
 */
class PaymentMethodsSection extends React.Component {
  state = {
    methods: [],
    defaultPaymentMethodId: null,
    loading: true,
    mutating: false,
    error: null,
    addDialogOpen: false,
    // The PM the user is about to detach. Null = no confirm dialog open.
    // Holding the whole row (not just id) lets the dialog show brand/last4
    // or Link email so the admin knows exactly which card they're removing.
    confirmDetach: null,
  };

  _mounted = false;
  _mutationRef = false;

  componentDidMount() {
    this._mounted = true;
    // Guard against the first mount happening before companyId is available.
    // Parents like LocationDetailView feed in `location?.companyId` which is
    // null on the very first render and populated a tick later — without
    // this guard the saga sends `companyId: undefined`, the server returns
    // an empty list, and the section sits stuck on "No saved methods" until
    // the user refreshes. componentDidUpdate picks up the late-arriving id.
    if (this.props.companyId) this.fetchMethods();
  }

  componentDidUpdate(prev) {
    // Refetch when the companyId prop arrives (or changes between locations
    // — admin switching between owned companies). Without this the
    // PaymentMethodsSection stays on its initial empty list. Also clear
    // the previous location's methods immediately so the user never sees
    // KINGDOM's cards while viewing GREEN HOUSE's detail page.
    if (prev.companyId !== this.props.companyId) {
      this._requestId = (this._requestId || 0) + 1;
      this.setSafe({
        methods: [],
        defaultPaymentMethodId: null,
        error: null,
        loading: !!this.props.companyId,
      });
      if (this.props.companyId) this.fetchMethods(this._requestId);
      return;
    }
    // Parent-driven refresh. The Pay dialog can attach a new card to the
    // customer (when the user ticks "Save card for future payments"),
    // but that happens outside this component's awareness — there's no
    // companyId change and no Add-Card flow we could hook into. The
    // parent bumps `refreshSignal` after a successful pay so we know to
    // re-fetch and surface the newly attached card without forcing a
    // page reload.
    if (
      prev.refreshSignal !== this.props.refreshSignal &&
      this.props.companyId
    ) {
      this.fetchMethods();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  setSafe = (next) => {
    if (this._mounted) this.setState(next);
  };

  fetchMethods = async (requestId) => {
    // Stale-response guard. If the user navigates A → B → A fast enough,
    // A's first fetch could resolve AFTER B's fetch and overwrite B's
    // state with A's methods. We tag each fetch with a monotonic id and
    // drop responses that don't match the latest.
    if (requestId == null) {
      this._requestId = (this._requestId || 0) + 1;
      requestId = this._requestId;
    }
    const companyIdAtStart = this.props.companyId;
    this.setSafe({ loading: true, error: null });
    try {
      const res = await AuthUtil.postRequest(
        STRIPE_ENDPOINTS.LIST_SAVED_CARDS,
        {
          companyId: companyIdAtStart,
        }
      );
      if (
        this._requestId !== requestId ||
        this.props.companyId !== companyIdAtStart
      ) {
        return; // stale — a newer fetch (or different company) has taken over
      }
      if (res?.data?.success) {
        this.setSafe({
          methods: res.data.methods || [],
          defaultPaymentMethodId: res.data.defaultPaymentMethodId || null,
          loading: false,
        });
      } else {
        this.setSafe({
          methods: [],
          loading: false,
          error: res?.data?.message || null,
        });
      }
    } catch (e) {
      if (this._requestId !== requestId) return;
      this.setSafe({
        loading: false,
        error: 'Could not load saved payment methods.',
      });
    }
  };

  withMutation = async (fn) => {
    // Single-mutation guard. Synchronous ref check + state flag prevents
    // rapid double-clicks from issuing two parallel detach / setDefault
    // requests.
    if (this._mutationRef) return;
    this._mutationRef = true;
    this.setSafe({ mutating: true, error: null });
    try {
      await fn();
      await this.fetchMethods();
    } catch (e) {
      this.setSafe({ error: 'Could not update payment method.' });
    } finally {
      this._mutationRef = false;
      this.setSafe({ mutating: false });
    }
  };

  handleSetDefault = (pmId) =>
    this.withMutation(() =>
      AuthUtil.postRequest(STRIPE_ENDPOINTS.SET_DEFAULT_CARD, {
        companyId: this.props.companyId,
        paymentMethodId: pmId,
      })
    );

  handleDetach = (method) => {
    this.setState({ confirmDetach: method });
  };

  confirmDetachNow = () => {
    const m = this.state.confirmDetach;
    if (!m) return;
    this.setState({ confirmDetach: null });
    this.withMutation(() =>
      AuthUtil.postRequest(STRIPE_ENDPOINTS.DETACH_PAYMENT_METHOD, {
        companyId: this.props.companyId,
        paymentMethodId: m.id,
      })
    );
  };

  brandLabel = (method) => {
    if (method.type === 'link' || method.walletType === 'link') return 'Link';
    if (method.walletType === 'apple_pay') return 'Apple Pay';
    if (method.walletType === 'google_pay') return 'Google Pay';
    const b = (method.brand || 'card').toString();
    return b.charAt(0).toUpperCase() + b.slice(1);
  };

  brandIcon = (method) => {
    if (method.type === 'link' || method.walletType === 'link')
      return <CreditCardIcon style={{ fontSize: 20, color: '#00d66f' }} />;
    if (method.walletType === 'apple_pay')
      return <AppleIcon style={{ fontSize: 20, color: '#0f172a' }} />;
    if (method.walletType === 'google_pay')
      return <AndroidIcon style={{ fontSize: 20, color: '#4285F4' }} />;
    return <CreditCardIcon style={{ fontSize: 20, color: '#4338ca' }} />;
  };

  detailLine = (method) => {
    // Link entries hide the card details from the merchant (they live in
    // the customer's Link account). Show their email instead so the admin
    // knows which Link user the auto-renewal will charge.
    if (method.type === 'link' || method.walletType === 'link') {
      return method.linkEmail || 'Link account';
    }
    return `Expires ${String(method.expMonth || '').padStart(2, '0')}/${
      method.expYear || ''
    }`;
  };

  render() {
    const C = this.props.colors || {};
    const cardStyle = this.props.cardStyle || {
      background: '#fff',
      borderRadius: 12,
      padding: 14,
      marginBottom: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      border: '1px solid #edf0f5',
    };

    const { methods, loading, error, mutating } = this.state;

    return (
      <div style={cardStyle}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ marginBottom: 10 }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.blueGray || '#8a9cc5',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Payment Methods
          </div>
          <Box display="flex" style={{ gap: 6 }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={mutating}
              onClick={() => this.setState({ addDialogOpen: true })}
              startIcon={<AddIcon style={{ fontSize: 16 }} />}
              style={{ fontSize: 12, textTransform: 'none', borderRadius: 6 }}
            >
              Add Card
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={2}
          >
            <CircularProgress size={20} />
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
              Loading…
            </span>
          </Box>
        )}

        {!loading && error && (
          <div
            style={{
              padding: 10,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            {error}
          </div>
        )}

        {!loading && methods.length === 0 && !error && (
          <div
            style={{
              padding: 16,
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: 13,
            }}
          >
            No saved payment methods yet.
            <br />
            <span style={{ fontSize: 12, color: '#b0b8cc' }}>
              Save a card to enable automatic monthly renewal on the 15th.
            </span>
          </div>
        )}

        {!loading && methods.length > 0 && (
          <div>
            {methods.map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 0',
                  borderBottom:
                    i < methods.length - 1 ? '1px solid #f0f1f4' : 'none',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {this.brandIcon(m)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}
                  >
                    {this.brandLabel(m)}
                    {m.last4 ? ` •••• ${m.last4}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {this.detailLine(m)}
                    {m.isDefault && (
                      <span
                        style={{
                          marginLeft: 8,
                          color: '#047857',
                          fontWeight: 700,
                        }}
                      >
                        · DEFAULT
                      </span>
                    )}
                  </div>
                </div>
                <IconButton
                  size="small"
                  onClick={() => this.handleSetDefault(m.id)}
                  disabled={mutating || m.isDefault}
                  title={m.isDefault ? 'Default' : 'Make default'}
                >
                  {m.isDefault ? (
                    <StarIcon style={{ fontSize: 18, color: '#f59e0b' }} />
                  ) : (
                    <StarBorderIcon
                      style={{ fontSize: 18, color: '#9ca3af' }}
                    />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => this.handleDetach(m)}
                  disabled={mutating}
                  title="Remove"
                >
                  <DeleteOutlineIcon
                    style={{ fontSize: 18, color: '#9ca3af' }}
                  />
                </IconButton>
              </div>
            ))}
          </div>
        )}

        <PayWithStripeDialog
          open={this.state.addDialogOpen}
          mode="save_card"
          companyId={this.props.companyId}
          onClose={() => this.setState({ addDialogOpen: false })}
          onSuccess={() => {
            this.setState({ addDialogOpen: false });
            this.fetchMethods();
            // Adding a card no longer implicitly enables autoRenewal —
            // that is a separate, explicit user toggle. The admin can
            // flip it on from the Membership / billing settings when
            // they're ready to authorise off-session auto-charges.
            if (this.props.onCardAdded) this.props.onCardAdded();
          }}
        />

        <Dialog
          open={!!this.state.confirmDetach}
          onClose={() => this.setState({ confirmDetach: null })}
          maxWidth="xs"
          fullWidth
          PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
          <Box style={{ padding: '20px 22px 8px', textAlign: 'center' }}>
            <Box
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 12,
              }}
            >
              <WarningAmberRoundedIcon
                style={{ fontSize: 30, color: '#dc2626' }}
              />
            </Box>
            <Typography
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: '#0f172a',
                marginBottom: 4,
              }}
            >
              Remove payment method?
            </Typography>
            <Typography
              style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}
            >
              The 15th-of-month auto-renewal will need a different saved card.
              You can add a new one anytime.
            </Typography>
          </Box>

          {this.state.confirmDetach && (
            <DialogContent style={{ padding: '0 22px 8px' }}>
              <Box
                style={{
                  background: '#f8fafc',
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  padding: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {this.brandIcon(this.state.confirmDetach)}
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}
                  >
                    {this.brandLabel(this.state.confirmDetach)}
                    {this.state.confirmDetach.last4
                      ? ` •••• ${this.state.confirmDetach.last4}`
                      : ''}
                  </Typography>
                  <Typography style={{ fontSize: 12, color: '#6b7280' }}>
                    {this.detailLine(this.state.confirmDetach)}
                  </Typography>
                </Box>
                {this.state.confirmDetach.isDefault && (
                  <Box
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: '#047857',
                      background: '#ecfdf5',
                      padding: '3px 6px',
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  >
                    DEFAULT
                  </Box>
                )}
              </Box>
            </DialogContent>
          )}

          <DialogActions style={{ padding: '12px 22px 20px', gap: 8 }}>
            <Button
              onClick={() => this.setState({ confirmDetach: null })}
              style={{
                flex: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 10,
                padding: '8px 0',
                color: '#475569',
                background: '#f1f5f9',
              }}
              disableElevation
            >
              Cancel
            </Button>
            <Button
              onClick={this.confirmDetachNow}
              variant="contained"
              disableElevation
              style={{
                flex: 1,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 10,
                padding: '8px 0',
                color: '#fff',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                boxShadow: '0 1px 3px rgba(220,38,38,0.35)',
              }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default PaymentMethodsSection;
